import { HttpInterceptorFn, HttpErrorResponse } from '@angular/common/http';
import { Auth } from 'aws-amplify';
import { from, throwError } from 'rxjs';
import { switchMap, catchError } from 'rxjs/operators';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { environment } from '../../../environments/environment';
import { UsersService } from '../services/users.service';

export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const router = inject(Router);
  const usersService = inject(UsersService);

  // Check if request should skip the interceptor
  if (req.headers.has('Skip-Interceptor')) {
    // Remove the skip header and proceed without adding auth
    const newReq = req.clone({
      headers: req.headers.delete('Skip-Interceptor')
    });
    return next(newReq);
  }

  // Helper function to create authenticated request
  const createAuthRequest = (token: string, username: string) => {
    // Check if the request is for users service
    const isUsersService = req.url.includes(environment.BASE_URL);
    
    console.log('Creating auth request with token:', token ? `${token.substring(0, 50)}...` : 'NO TOKEN');
    console.log('Username:', username);
    console.log('Request URL:', req.url);
    
    // Base headers for all requests
    const headers: { [key: string]: string } = {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json'
    };
    
    // Add username header only for users service requests
    if (isUsersService) {
      headers['username'] = username;
    }
    
    console.log('Request headers:', headers);
    
    return req.clone({
      setHeaders: headers
    });
  };

  // Helper function to handle token refresh and retry
  const handleTokenRefresh = (originalReq: any) => {
    return from(Auth.currentSession()).pipe(
      switchMap(session => {
        if (!session) {
          if (!environment.disableAuthGuard) {
            router.navigate(['/auth/login']);
          }
          return throwError(() => new Error('No session for refresh'));
        }

        const refreshToken = session.getRefreshToken().getToken();
        if (!refreshToken) {
          if (!environment.disableAuthGuard) {
            router.navigate(['/auth/login']);
          }
          return throwError(() => new Error('No refresh token available'));
        }
        
        // Call refresh token API
        return from(usersService.refreshToken(refreshToken)).pipe(
          switchMap(refreshResponse => {
            // Get the updated session after refresh
            return from(Auth.currentSession()).pipe(
              switchMap(newSession => {
                const newToken = newSession.getIdToken().getJwtToken();
                const username = newSession.getIdToken().payload['email'] || newSession.getIdToken().payload['cognito:username'];
                
                // Retry the original request with new token
                const retryReq = createAuthRequest(newToken, username);
                return next(retryReq);
              }),
              catchError(newSessionError => {
                console.error('Failed to get new session after refresh:', newSessionError);
                if (!environment.disableAuthGuard) {
                  router.navigate(['/auth/login']);
                }
                return throwError(() => newSessionError);
              })
            );
          }),
          catchError(refreshError => {
            console.error('Token refresh failed:', refreshError);
            if (!environment.disableAuthGuard) {
              router.navigate(['/auth/login']);
            }
            return throwError(() => refreshError);
          })
        );
      }),
      catchError(sessionError => {
        console.error('Session error during refresh attempt:', sessionError);
        if (!environment.disableAuthGuard) {
          router.navigate(['/auth/login']);
        }
        return throwError(() => sessionError);
      })
    );
  };

  return from(Auth.currentSession()).pipe(
    switchMap(session => {
      // Check if session exists and is valid
      if (!session) {
        console.warn('No session available');
        if (!environment.disableAuthGuard) {
          router.navigate(['/auth/login']);
        }
        return throwError(() => new Error('No session available'));
      }

      const token = session.getIdToken().getJwtToken();
      const username = session.getIdToken().payload['email'] || session.getIdToken().payload['cognito:username'];
      
      if (!token) {
        console.warn('No token available in session');
        if (!environment.disableAuthGuard) {
          router.navigate(['/auth/login']);
        }
        return throwError(() => new Error('No token available'));
      }

      const authReq = createAuthRequest(token, username);

      // Execute request and handle response
      return next(authReq).pipe(
        catchError((error: HttpErrorResponse) => {
          // Only handle 401 errors for our API endpoints
          const isOurAPI = req.url.includes(environment.BASE_URL);

          // Only handle 401 Unauthorized errors (token expired/invalid)
          if (error.status === 401 && isOurAPI && !req.headers.has('Retry-After-Refresh')) {
            console.log('401 Unauthorized detected, attempting token refresh...');
            
            // Mark request to prevent infinite loops
            const retryReq = req.clone({
              setHeaders: { 'Retry-After-Refresh': 'true' }
            });
            
            return handleTokenRefresh(retryReq);
          }
          
          // For ALL other errors (502, 500, 403, etc.), just propagate them
          // Do NOT redirect to login for server errors, network errors, etc.
          console.log(`HTTP Error ${error.status}: ${error.message} - Not handling as auth error`);
          return throwError(() => error);
        })
      );
    }),
    catchError(sessionError => {
      // Handle initial session errors
      console.error('Failed to get current session:', sessionError);
      if (!environment.disableAuthGuard) {
        router.navigate(['/auth/login']);
      }
      return throwError(() => sessionError);
    })
  );
};
